import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/context/AuthContext';
import { useOfflineSync } from '@/hooks/useOfflineSync';
import { toast } from 'sonner';
import { ArrowLeft, MapPin, Bell, Calendar as CalendarIcon, TrendingUp, Info, Image, Mic, X, Upload, Play, Pause, BarChart3, Plus, Trash2, WifiOff, CloudOff, RefreshCw, Cloud } from 'lucide-react';
import { format, addDays } from 'date-fns';
import { fr } from 'date-fns/locale';

const CreatePostPage = () => {
  const navigate = useNavigate();
  const { user, api } = useAuth();
  const { isOffline, pendingPosts, savePostOffline, syncPendingItems, isSyncing, retryPostSync, deletePendingPostById } = useOfflineSync();
  const [loading, setLoading] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingAudio, setUploadingAudio] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showPendingPosts, setShowPendingPosts] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioPlayerRef = useRef(null);
  const imageInputRef = useRef(null);
  const audioInputRef = useRef(null);

  const [form, setForm] = useState({
    title: '',
    content: '',
    category: '',
    commune: user?.commune || '',
    quartier: '',
    image_url: '',
    audio_url: '',
    event_date: null
  });

  // Poll state
  const [isPoll, setIsPoll] = useState(false);
  const [pollOptions, setPollOptions] = useState(['', '']);
  const [pollEndDate, setPollEndDate] = useState(addDays(new Date(), 7));

  const categories = [
    { id: 'alerte', name: 'Alerte', icon: Bell, description: 'Coupure eau/élec, accident, cyclone...' },
    { id: 'evenement', name: 'Événement', icon: CalendarIcon, description: 'Concert, fête, marché...' },
    { id: 'bon_plan', name: 'Bon Plan', icon: TrendingUp, description: 'Promo, réduction, gratuit...' },
    { id: 'info', name: 'Info', icon: Info, description: 'Information générale' }
  ];

  const communes = [
    // Nord
    { id: 'Saint-Denis', name: 'Saint-Denis', quartiers: ['Centre-ville', 'Sainte-Clotilde', 'Le Chaudron', 'La Montagne', 'Bas de la Rivière', 'Bellepierre'] },
    { id: 'Sainte-Marie', name: 'Sainte-Marie', quartiers: ['Centre-ville', 'La Rivière des Pluies', 'Grande Montée', 'Les Fonds Jardin'] },
    { id: 'Sainte-Suzanne', name: 'Sainte-Suzanne', quartiers: ['Centre-ville', 'Quartier Français', 'Bagatelle', 'Bel Air'] },
    // Est
    { id: 'Saint-André', name: 'Saint-André', quartiers: ['Centre-ville', 'Cambuston', 'Champ Borne', 'Petit Bazar'] },
    { id: 'Bras-Panon', name: 'Bras-Panon', quartiers: ['Centre-ville', 'Rivière des Roches', 'Rivière du Mât'] },
    { id: 'Saint-Benoît', name: 'Saint-Benoît', quartiers: ['Centre-ville', 'Sainte-Anne', 'Bourbier', 'Beaulieu'] },
    { id: 'Sainte-Rose', name: 'Sainte-Rose', quartiers: ['Centre-ville', 'Piton Sainte-Rose', 'La Marine', 'Bois Blanc'] },
    { id: 'Plaine-des-Palmistes', name: 'La Plaine-des-Palmistes', quartiers: ['Centre-ville', 'Premier Village', 'Petite Plaine'] },
    // Sud
    { id: 'Saint-Pierre', name: 'Saint-Pierre', quartiers: ['Centre-ville', 'Terre Sainte', 'Ravine des Cabris', 'Pierrefonds', 'Bois d\'Olives'] },
    { id: 'Saint-Louis', name: 'Saint-Louis', quartiers: ['Centre-ville', 'La Rivière', 'Les Makes', 'Le Gol'] },
    { id: 'Saint-Joseph', name: 'Saint-Joseph', quartiers: ['Centre-ville', 'Langevin', 'Vincendo', 'Manapany'] },
    { id: 'Saint-Philippe', name: 'Saint-Philippe', quartiers: ['Centre-ville', 'Le Baril', 'Basse Vallée', 'Cap Méchant'] },
    { id: 'Petite-Île', name: 'Petite-Île', quartiers: ['Centre-ville', 'Grand Anse', 'Manapany-les-Bains'] },
    { id: 'Le-Tampon', name: 'Le Tampon', quartiers: ['Centre-ville', 'Trois Mares', 'La Plaine des Cafres', 'Bras de Pontho', 'Le 14ème'] },
    { id: 'Entre-Deux', name: 'Entre-Deux', quartiers: ['Centre-ville', 'Le Dimitile'] },
    { id: 'Cilaos', name: 'Cilaos', quartiers: ['Centre-ville', 'Îlet à Cordes', 'Bras Sec', 'Peter Both'] },
    { id: 'Étang-Salé', name: "L'Étang-Salé", quartiers: ['Centre-ville', 'Les Avirons', 'Étang-Salé les Bains'] },
    { id: 'Les-Avirons', name: 'Les Avirons', quartiers: ['Centre-ville', 'Tévelave'] },
    // Ouest
    { id: 'Saint-Paul', name: 'Saint-Paul', quartiers: ['Centre-ville', 'Saint-Gilles', 'L\'Ermitage', 'La Saline', 'Le Guillaume', 'Boucan Canot'] },
    { id: 'Saint-Leu', name: 'Saint-Leu', quartiers: ['Centre-ville', 'La Chaloupe', 'Piton Saint-Leu', 'Les Colimaçons'] },
    { id: 'Trois-Bassins', name: 'Trois-Bassins', quartiers: ['Centre-ville', 'Grande Ravine', 'Petite Ravine'] },
    { id: 'La-Possession', name: 'La Possession', quartiers: ['Centre-ville', 'Sainte-Thérèse', 'Dos d\'Âne', 'Rivière des Galets'] },
    { id: 'Le-Port', name: 'Le Port', quartiers: ['Centre-ville', 'ZAC', 'Rivière des Galets'] },
    // Cirque
    { id: 'Salazie', name: 'Salazie', quartiers: ['Hell-Bourg', 'Mare à Vieille Place', 'Grand Îlet', 'Mare à Poule d\'Eau'] }
  ];

  const selectedCommuneData = communes.find(c => c.id === form.commune);

  // Image Upload Handler
  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Veuillez sélectionner une image');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image trop volumineuse (max 10MB)');
      return;
    }

    setUploadingImage(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await api.post('/upload/image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      setForm({ ...form, image_url: `${backendUrl}${response.data.url}` });
      toast.success('Image uploadée !');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'upload');
    } finally {
      setUploadingImage(false);
    }
  };

  // Audio Upload Handler
  const handleAudioUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('audio/')) {
      toast.error('Veuillez sélectionner un fichier audio');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error('Audio trop volumineux (max 10MB)');
      return;
    }

    setUploadingAudio(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await api.post('/upload/audio', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      setForm({ ...form, audio_url: `${backendUrl}${response.data.url}` });
      toast.success('Audio uploadé !');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'upload');
    } finally {
      setUploadingAudio(false);
    }
  };

  // Voice Recording
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (e) => {
        audioChunksRef.current.push(e.data);
      };

      mediaRecorderRef.current.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        const file = new File([audioBlob], 'recording.webm', { type: 'audio/webm' });
        
        setUploadingAudio(true);
        const formData = new FormData();
        formData.append('file', file);

        try {
          const response = await api.post('/upload/audio', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          const backendUrl = process.env.REACT_APP_BACKEND_URL;
          setForm(prev => ({ ...prev, audio_url: `${backendUrl}${response.data.url}` }));
          toast.success('Enregistrement uploadé !');
        } catch (error) {
          toast.error('Erreur lors de l\'upload');
        } finally {
          setUploadingAudio(false);
        }

        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
      toast.info('Enregistrement en cours...');
    } catch (error) {
      toast.error('Impossible d\'accéder au microphone');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const toggleAudioPlayback = () => {
    if (audioPlayerRef.current) {
      if (isPlaying) {
        audioPlayerRef.current.pause();
      } else {
        audioPlayerRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.title.trim()) {
      toast.error('Le titre est requis');
      return;
    }
    if (!form.content.trim()) {
      toast.error('Le contenu est requis');
      return;
    }
    if (!form.category) {
      toast.error('Sélectionnez une catégorie');
      return;
    }
    if (!form.commune) {
      toast.error('Sélectionnez une commune');
      return;
    }

    // Validate poll options
    if (isPoll) {
      const validOptions = pollOptions.filter(opt => opt.trim());
      if (validOptions.length < 2) {
        toast.error('Minimum 2 options pour le sondage');
        return;
      }
    }

    const postData = {
      ...form,
      event_date: form.event_date ? form.event_date.toISOString() : null,
      poll_options: isPoll ? pollOptions.filter(opt => opt.trim()) : null,
      poll_end_date: isPoll ? pollEndDate.toISOString() : null
    };

    // If offline, save locally
    if (isOffline) {
      try {
        await savePostOffline(postData);
        toast.success('Post sauvegardé hors ligne !', {
          description: 'Il sera publié automatiquement à la reconnexion.'
        });
        navigate('/feed');
      } catch (error) {
        toast.error('Erreur lors de la sauvegarde');
      }
      return;
    }

    // Online - publish directly
    setLoading(true);

    try {
      await api.post('/posts', postData);
      toast.success('Post publié avec succès !');
      navigate('/feed');
    } catch (error) {
      // If network error, offer offline save
      if (!navigator.onLine || error.code === 'ECONNABORTED' || error.message === 'Network Error') {
        try {
          await savePostOffline(postData);
          toast.info('Connexion perdue - post sauvegardé hors ligne');
          navigate('/feed');
        } catch (offlineError) {
          toast.error('Erreur lors de la sauvegarde');
        }
      } else {
        toast.error(error.response?.data?.detail || 'Erreur lors de la publication');
      }
    } finally {
      setLoading(false);
    }
  };

  // Poll handlers
  const addPollOption = () => {
    if (pollOptions.length < 6) {
      setPollOptions([...pollOptions, '']);
    }
  };

  const removePollOption = (index) => {
    if (pollOptions.length > 2) {
      setPollOptions(pollOptions.filter((_, i) => i !== index));
    }
  };

  const updatePollOption = (index, value) => {
    const newOptions = [...pollOptions];
    newOptions[index] = value;
    setPollOptions(newOptions);
  };

  return (
    <div className="min-h-screen tropic-bg">
      {/* Header */}
      <header className="sticky top-0 z-40 glass border-b">
        <div className="container mx-auto max-w-2xl px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="icon"
                onClick={() => navigate(-1)}
                data-testid="back-btn"
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <h1 className="text-xl font-bold">Nouveau post</h1>
            </div>
            
            {/* Offline Indicator & Pending Posts */}
            <div className="flex items-center gap-2">
              {isOffline && (
                <Badge variant="outline" className="bg-orange-500/10 text-orange-600 border-orange-200">
                  <WifiOff className="h-3 w-3 mr-1" />
                  Hors ligne
                </Badge>
              )}
              
              {pendingPosts.length > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowPendingPosts(!showPendingPosts)}
                  className="relative"
                  data-testid="pending-posts-btn"
                >
                  <CloudOff className="h-4 w-4 mr-1" />
                  {pendingPosts.length}
                  <span className="absolute -top-1 -right-1 h-2 w-2 bg-orange-500 rounded-full animate-pulse" />
                </Button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Pending Posts Panel */}
      {showPendingPosts && pendingPosts.length > 0 && (
        <div className="container mx-auto max-w-2xl px-4 py-4">
          <Card className="border-orange-200 bg-orange-50/50 dark:bg-orange-950/20">
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CloudOff className="h-5 w-5 text-orange-600" />
                  <h3 className="font-medium text-orange-800 dark:text-orange-200">
                    Posts en attente ({pendingPosts.length})
                  </h3>
                </div>
                {!isOffline && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={syncPendingItems}
                    disabled={isSyncing}
                    data-testid="sync-all-btn"
                  >
                    {isSyncing ? (
                      <RefreshCw className="h-4 w-4 animate-spin" />
                    ) : (
                      <>
                        <Cloud className="h-4 w-4 mr-1" />
                        Synchroniser
                      </>
                    )}
                  </Button>
                )}
              </div>
              
              <div className="space-y-2">
                {pendingPosts.slice(0, 3).map((post) => (
                  <div 
                    key={post.local_id} 
                    className="flex items-center justify-between p-3 bg-white dark:bg-background rounded-lg border"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{post.title}</p>
                      <p className="text-xs text-muted-foreground">
                        {post.status === 'failed' ? (
                          <span className="text-red-500">Échec - {post.retry_count} tentative(s)</span>
                        ) : (
                          `En attente • ${new Date(post.created_at).toLocaleString('fr-FR')}`
                        )}
                      </p>
                    </div>
                    <div className="flex items-center gap-1">
                      {!isOffline && post.status === 'failed' && (
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8"
                          onClick={() => retryPostSync(post.local_id)}
                          data-testid={`retry-${post.local_id}`}
                        >
                          <RefreshCw className="h-4 w-4" />
                        </Button>
                      )}
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8 text-destructive"
                        onClick={() => deletePendingPostById(post.local_id)}
                        data-testid={`delete-pending-${post.local_id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
                {pendingPosts.length > 3 && (
                  <p className="text-xs text-center text-muted-foreground">
                    + {pendingPosts.length - 3} autre(s)
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Main Content */}
      <main className="container mx-auto max-w-2xl px-4 py-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Category Selection */}
          <div className="space-y-3">
            <Label>Catégorie *</Label>
            <div className="grid grid-cols-2 gap-3">
              {categories.map((cat) => (
                <Card 
                  key={cat.id}
                  className={`cursor-pointer transition-all ${
                    form.category === cat.id 
                      ? 'border-primary ring-2 ring-primary/20' 
                      : 'hover:border-primary/50'
                  }`}
                  onClick={() => setForm({ ...form, category: cat.id })}
                  data-testid={`category-${cat.id}`}
                >
                  <CardContent className="p-4 flex items-center gap-3">
                    <cat.icon className={`h-5 w-5 ${
                      cat.id === 'alerte' ? 'text-red-500' :
                      cat.id === 'evenement' ? 'text-blue-500' :
                      cat.id === 'bon_plan' ? 'text-green-500' :
                      'text-orange-500'
                    }`} />
                    <div>
                      <p className="font-medium text-sm">{cat.name}</p>
                      <p className="text-xs text-muted-foreground">{cat.description}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Title */}
          <div className="space-y-2">
            <Label htmlFor="title">Titre *</Label>
            <Input
              id="title"
              placeholder="Ex: Coupure d'eau demain à Sainte-Clotilde"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              maxLength={100}
              data-testid="title-input"
            />
            <p className="text-xs text-muted-foreground text-right">{form.title.length}/100</p>
          </div>

          {/* Content */}
          <div className="space-y-2">
            <Label htmlFor="content">Description *</Label>
            <Textarea
              id="content"
              placeholder="Décrivez votre alerte, événement ou bon plan en détail..."
              value={form.content}
              onChange={(e) => setForm({ ...form, content: e.target.value })}
              rows={5}
              maxLength={1000}
              data-testid="content-input"
            />
            <p className="text-xs text-muted-foreground text-right">{form.content.length}/1000</p>
          </div>

          {/* Location */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Commune *</Label>
              <Select 
                value={form.commune}
                onValueChange={(value) => setForm({ ...form, commune: value, quartier: '' })}
              >
                <SelectTrigger data-testid="commune-select">
                  <SelectValue placeholder="Sélectionner" />
                </SelectTrigger>
                <SelectContent>
                  {communes.map((commune) => (
                    <SelectItem key={commune.id} value={commune.id}>
                      <div className="flex items-center gap-2">
                        <MapPin className="h-3 w-3" />
                        {commune.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Quartier (optionnel)</Label>
              <Select 
                value={form.quartier}
                onValueChange={(value) => setForm({ ...form, quartier: value })}
                disabled={!form.commune}
              >
                <SelectTrigger data-testid="quartier-select">
                  <SelectValue placeholder="Sélectionner" />
                </SelectTrigger>
                <SelectContent>
                  {selectedCommuneData?.quartiers.map((quartier) => (
                    <SelectItem key={quartier} value={quartier}>
                      {quartier}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Event Date (for events) */}
          {form.category === 'evenement' && (
            <div className="space-y-2">
              <Label>Date de l'événement</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-start text-left font-normal"
                    data-testid="event-date-btn"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {form.event_date 
                      ? format(form.event_date, 'PPP', { locale: fr })
                      : 'Sélectionner une date'
                    }
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={form.event_date}
                    onSelect={(date) => setForm({ ...form, event_date: date })}
                    disabled={(date) => date < new Date()}
                    locale={fr}
                  />
                </PopoverContent>
              </Popover>
            </div>
          )}

          {/* Media Upload Section */}
          <div className="space-y-4">
            <Label>Médias (optionnel)</Label>
            
            {/* Image Upload */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1"
                  onClick={() => imageInputRef.current?.click()}
                  disabled={uploadingImage}
                  data-testid="upload-image-btn"
                >
                  {uploadingImage ? (
                    <span className="spinner mr-2" />
                  ) : (
                    <Image className="h-4 w-4 mr-2" />
                  )}
                  {uploadingImage ? 'Upload...' : 'Ajouter une photo'}
                </Button>
                <input
                  ref={imageInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleImageUpload}
                />
              </div>
              
              {form.image_url && (
                <div className="relative rounded-xl overflow-hidden group">
                  <img 
                    src={form.image_url} 
                    alt="Aperçu"
                    className="w-full h-48 object-cover"
                  />
                  <Button
                    type="button"
                    variant="destructive"
                    size="icon"
                    className="absolute top-2 right-2 h-8 w-8 z-10 shadow-lg"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setForm({ ...form, image_url: '' });
                    }}
                    data-testid="remove-image-btn"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>

            {/* Audio Upload */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1"
                  onClick={() => audioInputRef.current?.click()}
                  disabled={uploadingAudio || isRecording}
                  data-testid="upload-audio-btn"
                >
                  {uploadingAudio ? (
                    <span className="spinner mr-2" />
                  ) : (
                    <Upload className="h-4 w-4 mr-2" />
                  )}
                  {uploadingAudio ? 'Upload...' : 'Ajouter un audio'}
                </Button>
                <input
                  ref={audioInputRef}
                  type="file"
                  accept="audio/*"
                  className="hidden"
                  onChange={handleAudioUpload}
                />
                
                {/* Voice Recording Button */}
                <Button
                  type="button"
                  variant={isRecording ? "destructive" : "outline"}
                  onClick={isRecording ? stopRecording : startRecording}
                  disabled={uploadingAudio}
                  data-testid="record-audio-btn"
                >
                  <Mic className={`h-4 w-4 ${isRecording ? 'animate-pulse' : ''}`} />
                  {isRecording ? 'Stop' : ''}
                </Button>
              </div>

              {form.audio_url && (
                <div className="flex items-center gap-3 p-4 bg-muted rounded-xl">
                  <Button
                    type="button"
                    variant="secondary"
                    size="icon"
                    onClick={toggleAudioPlayback}
                    data-testid="play-audio-btn"
                  >
                    {isPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                  </Button>
                  <div className="flex-1">
                    <p className="text-sm font-medium">Audio attaché</p>
                    <audio 
                      ref={audioPlayerRef}
                      src={form.audio_url}
                      onEnded={() => setIsPlaying(false)}
                      className="w-full mt-2"
                      controls
                    />
                  </div>
                  <Button
                    type="button"
                    variant="destructive"
                    size="icon"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setForm({ ...form, audio_url: '' });
                    }}
                    data-testid="remove-audio-btn"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>
          </div>

          {/* Poll Section */}
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <BarChart3 className="h-5 w-5 text-primary" />
                  <Label className="font-medium">Sondage</Label>
                </div>
                <Switch 
                  checked={isPoll}
                  onCheckedChange={setIsPoll}
                  data-testid="poll-switch"
                />
              </div>

              {isPoll && (
                <div className="space-y-4">
                  <div className="space-y-3">
                    {pollOptions.map((option, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <Input
                          placeholder={`Option ${index + 1}`}
                          value={option}
                          onChange={(e) => updatePollOption(index, e.target.value)}
                          className="flex-1"
                          data-testid={`poll-option-${index}`}
                        />
                        {pollOptions.length > 2 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => removePollOption(index)}
                            data-testid={`remove-option-${index}`}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>

                  {pollOptions.length < 6 && (
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full"
                      onClick={addPollOption}
                      data-testid="add-option-btn"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Ajouter une option
                    </Button>
                  )}

                  <div className="space-y-2">
                    <Label>Fin du sondage</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button 
                          variant="outline" 
                          className="w-full justify-start"
                          data-testid="poll-end-date-btn"
                        >
                          <CalendarIcon className="h-4 w-4 mr-2" />
                          {format(pollEndDate, 'PPP', { locale: fr })}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={pollEndDate}
                          onSelect={(date) => date && setPollEndDate(date)}
                          locale={fr}
                          disabled={(date) => date < new Date()}
                          data-testid="poll-end-calendar"
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Submit Button */}
          <Button 
            type="submit" 
            className={`w-full rounded-xl py-6 text-lg ${isOffline ? 'bg-orange-600 hover:bg-orange-700' : ''}`}
            disabled={loading || uploadingImage || uploadingAudio}
            data-testid="submit-btn"
          >
            {loading ? (
              <>
                <span className="spinner mr-2" />
                Publication...
              </>
            ) : isOffline ? (
              <>
                <CloudOff className="h-5 w-5 mr-2" />
                Sauvegarder hors ligne
              </>
            ) : (
              <>
                <Cloud className="h-5 w-5 mr-2" />
                Publier
              </>
            )}
          </Button>
        </form>
      </main>
    </div>
  );
};

export default CreatePostPage;
